import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import SignupForm from './components/SignupForm';
import SigninForm from './components/SigninForm';
import SubmissionForm from './components/SubmissionForm';
import SubmissionsList from './components/SubmissionsList';
import DeadlineTimer from './components/DeadlineTimer';
import AllSubmissions from './pages/AllSubmissions';
import DemoBanner from './components/DemoBanner';
import { auth } from './utils/auth';
import { api } from './utils/api';

function LandingPage() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      const result = await api.submitEarlySignup(email);
      if (result.success) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Failed to submit email');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit email. Please try again.');
      console.error('Error submitting email:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-16">
        <div className="flex flex-col md:flex-row gap-8 md:gap-16">
          <div className="flex-1 max-w-xl">
            <h1 className="text-2xl font-mono font-light text-neutral-900 mb-2">
              first draft
            </h1>
            <p className="text-sm font-mono text-neutral-600 mb-6 leading-relaxed">
              A pre-committment mechanism for writers, bloggers and posters with guilty-feeling drafts folders.
            </p>

            <p className="text-sm font-mono text-neutral-700 mb-2">To stay in the cohort, publish:</p>
            <ul className="text-sm font-mono text-neutral-600 mb-6 pl-6 space-y-1" style={{ listStyle: 'none' }}>
              <li className="relative before:content-['–'] before:absolute before:-left-4">by midnight (your timezone),</li>
              <li className="relative before:content-['–'] before:absolute before:-left-4">every day,</li>
              <li className="relative before:content-['–'] before:absolute before:-left-4">for 2 weeks.</li>
            </ul>

            <p className="text-sm font-mono text-neutral-600 mb-4 leading-relaxed">
              We accept links to <span className="underline decoration-dotted" title="exact definition up to you, but more than 100 words">blog-length</span> writing anywhere on the public internet.
            </p>

            <p className="text-sm font-mono text-neutral-600 mb-4 leading-relaxed">
              There's a discord for feedback and co-working.
            </p>

            <p className="text-sm font-mono text-neutral-600 mb-4 leading-relaxed">
              Cohort starts 2025-10-17, we can email you a signup link the day before it starts.
              A published post is worth 10 in the drafts. Good luck!<br/>
            </p>

            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your email"
                required
                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                className="flex-1 border border-neutral-300 px-4 py-3 text-sm font-mono text-neutral-900 focus:outline-none focus:border-neutral-900 disabled:bg-neutral-100 disabled:text-neutral-500"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                className="border border-neutral-900 px-6 py-3 text-sm font-mono text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-neutral-900"
              >
                {submitStatus === 'loading' ? 'sending...' : submitStatus === 'success' ? 'sent!' : 'email me'}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-sm font-mono text-green-600 mt-2">
                Thanks! We'll email you soon.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="text-sm font-mono text-red-600 mt-2">
                {errorMessage}
              </p>
            )}
          </div>

          <div className="flex-1 border border-neutral-300 aspect-square md:aspect-auto min-h-[200px] md:min-h-[400px] flex items-center justify-center bg-neutral-50">
            <span className="text-xs font-mono text-neutral-400 italic">[image placeholder]</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface User {
  id: number;
  name: string;
  email: string;
  token?: string;
}

function HomePage() {
  const [user, setUser] = useState<User | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([
  ]);
  const [showSignin, setShowSignin] = useState(false);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [submittedTodayCount, setSubmittedTodayCount] = useState(0);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      fetchParams();
    }
  }, []);

  const fetchParams = async () => {
    try {
      const userResult = await api.getUser();
      const user = userResult.user;
      const entriesResult = await api.getUserEntries();
      const deadlineResult = await api.getDeadline();
      const submittedTodayResult = await api.getSubmittedToday();

      setUser({id: user.user_id, email: user.email, name: user.name});
      if (entriesResult.entries) {
        setSubmissions(entriesResult.entries);
      }
      if (deadlineResult.deadline) {
        setDeadline(new Date(deadlineResult.deadline));
      }
      setSubmittedTodayCount(submittedTodayResult.count);
    } catch (error) {
      console.error('Failed to fetch user data, clearing invalid token:', error);
      auth.clearToken();
      setUser(null);
      setDeadline(null);
      setSubmittedTodayCount(0);
    }
  }


  const handleLogout = () => {
    auth.clearToken();
    setUser(null);
    setSubmissions([]);
  };

  return (
    <div className="min-h-screen bg-white">
      <DemoBanner />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-12">
          <h1 className="text-2xl font-mono font-light text-neutral-900">
            first draft
          </h1>
          <h2 className="text-sm font-mono text-neutral-600">show your ideas to the world</h2>
          <Link
            to="/all"
            className="text-xs font-mono text-neutral-500 underline hover:text-neutral-700 mt-2 inline-block"
          >
            view all submissions →
          </Link>
          {user && (
            <div className="mt-2 flex items-center gap-4">
              <span className="text-xs font-mono text-neutral-500">logged in as {user.name}</span>
              <button
                onClick={handleLogout}
                className="text-xs font-mono text-neutral-500 underline hover:text-neutral-700"
              >
                logout
              </button>
            </div>
          )}
        </header>

        <main>
          {user && deadline && (
            <DeadlineTimer deadline={deadline} submittedTodayCount={submittedTodayCount} totalCount={0} />
          )}

          {!user ? (
            <div className="max-w-sm">
              <div className="border border-neutral-300 p-6">
                <h2 className="text-sm font-mono text-neutral-900 mb-6">
                  {showSignin ? 'sign in' : 'signup'}
                </h2>
                {showSignin ? (
                  <SigninForm onSignin={(userData) => {
                    setUser(userData);
                    fetchParams();
                  }} />
                ) : (
                  <SignupForm onSignup={(userData) => {
                    setUser(userData);
                    fetchParams();
                  }} />
                )}
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowSignin(!showSignin)}
                    className="text-xs font-mono text-neutral-500 underline hover:text-neutral-700"
                  >
                    {showSignin ? 'need to sign up?' : 'already have an account?'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="border border-neutral-300 p-6">
                  <h2 className="text-sm font-mono text-neutral-900 mb-6">submit</h2>
                  <SubmissionForm
                    onSubmit={(submission) => {
                      setSubmissions([submission, ...submissions]);
                      fetchParams();
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="border border-neutral-300 p-6">
                  <h2 className="text-sm font-mono text-neutral-900 mb-6">submissions</h2>
                  <SubmissionsList submissions={submissions} />
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/app" element={<HomePage />} />
        <Route path="/all" element={<AllSubmissions />} />
      </Routes>
    </Router>
  );
}

export default App;
